import { definedMap, withDefault } from "collection-utils";

import { SerializedComponent } from "../../components/SerializedApp";
import { ValueProducer } from "../../components/types";
import { rowIndexColumnName } from "../../google-sheets";
import {
    ComponentBuilder,
    ComponentConfiguratorContext,
    PropertyDescriptor,
    PropertyTableGetter,
    makeStringyColumnPropertyDescriptor,
} from "../components";
import { CalendarListMode } from "../components/calendar-list-mode";
import { makeTitlePropertyDescriptor } from "../components/descriptor-utils";
import { PropertySection } from "../components/property-sections";
import { getColumnProperty, getEnumProperty } from "../components/property-utils";
import {
    ArrayContentDescription,
    ArrayScreenDescription,
    ArrayScreenFormat,
    LegacyPropertyDescription,
    MutatingScreenKind,
    PropertyDescription,
    PropertyKind,
    ScreenDescriptionKind,
    TableGlideType,
    getPrimitiveNonHiddenColumns,
    getTableColumn,
} from "../description";
import { getPropertyWithColumnFormat } from "../formulas";
import { DateTimeParts, decomposeFormatFormula, ValueFormatKind } from "../formulas/formula-specifications";
import { InlineListBuild, makeEmptyMessage } from "./array-content";
import { ArrayScreenHandlerBase } from "./array-screen";
import { CalendarListOrder } from "./calendar-list-order";

interface CalendarListArrayContentDescription extends ArrayContentDescription {
    readonly titleProperty: LegacyPropertyDescription;
    readonly descriptionProperty: PropertyDescription | undefined;

    readonly dateTimeProperty: LegacyPropertyDescription;
    readonly endProperty: PropertyDescription | undefined;

    readonly order: PropertyDescription | undefined;
    readonly defaultModeProperty: PropertyDescription | undefined;
}

interface CalendarListArrayScreenDescription extends ArrayScreenDescription, CalendarListArrayContentDescription {
    readonly kind: ScreenDescriptionKind.Array;
    readonly format: ArrayScreenFormat.CalendarList;
}

const dateTimeProperties: ReadonlyArray<string> = ["date", "date-time", "datetime", "when", "time"];

export function makeEndDateTimePropertyDescriptor<T extends ArrayContentDescription>(
    getPropertyTable: PropertyTableGetter<T> | undefined,
    section: PropertySection
): PropertyDescriptor {
    return {
        kind: PropertyKind.Column,
        property: { name: "endProperty" },
        section,
        label: "End",
        required: false,
        editable: true,
        searchable: false,
        getIndirectTable: getPropertyTable,
        getAllowedColumns: getPrimitiveNonHiddenColumns,
        preferredNames: ["end", "finish", "event end", "event finish"],
        preferredType: "date-time",
    };
}

export function makeDescriptionPropertyDescriptor<T extends ArrayContentDescription>(
    getPropertyTable: PropertyTableGetter<T> | undefined,
    section: PropertySection
): PropertyDescriptor {
    return {
        kind: PropertyKind.Column,
        property: { name: "descriptionProperty" },
        section,
        label: "Description",
        required: false,
        editable: true,
        searchable: true,
        getIndirectTable: getPropertyTable,
        getAllowedColumns: getPrimitiveNonHiddenColumns,
        preferredNames: ["description", "subtitle", "caption"],
        preferredType: "string",
    };
}

export class CalendarListArrayScreenHandler extends ArrayScreenHandlerBase<
    CalendarListArrayContentDescription,
    CalendarListArrayScreenDescription
> {
    constructor() {
        super(ArrayScreenFormat.CalendarList, "Calendar", "calendar");
    }

    protected makeDetailScreenTitle(
        builder: ComponentBuilder,
        desc: CalendarListArrayContentDescription,
        table: TableGlideType,
        ctx: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;
        const titleProperty = getColumnProperty(desc.titleProperty);
        if (titleProperty === undefined) return b.localizedString("data");
        return b.getProperty(titleProperty, ctx, table);
    }

    public makeContentForProcessedArray(
        builder: ComponentBuilder,
        desc: CalendarListArrayContentDescription,
        table: TableGlideType,
        arrayData: SerializedComponent,
        searchVariable: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        _uniqueID: string,
        caption: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;

        const title = definedMap(getColumnProperty(desc.titleProperty), n =>
            getPropertyWithColumnFormat(builder, table, n)
        );
        const description = definedMap(getColumnProperty(desc.descriptionProperty), n =>
            getPropertyWithColumnFormat(builder, table, n)
        );
        const dateTimeProperty = getColumnProperty(desc.dateTimeProperty);
        const endDateTimeProperty = getColumnProperty(desc.endProperty);
        if (title === undefined || dateTimeProperty === undefined) return b.emptyUI;

        const dateTimeColumn = getTableColumn(table, dateTimeProperty);
        if (dateTimeColumn === undefined) return b.emptyUI;

        // ##showTimeInCalendar:
        // We don't want to show the time in the Calendar list if the column's
        // format is set to "Date only".  See
        // https://github.com/quicktype/glide/issues/9711
        let showTime = true;
        const formatSpec = definedMap(dateTimeColumn.displayFormula, decomposeFormatFormula);
        if (formatSpec?.kind === ValueFormatKind.DateTime) {
            showTime = formatSpec.parts !== DateTimeParts.DateOnly;
        }

        const onTap = this.makeDefaultAction(builder, desc, table);

        const defaultModeProperty = withDefault(getEnumProperty(desc.defaultModeProperty), CalendarListMode.List);
        const order = b.constant(withDefault(getEnumProperty(desc.order), CalendarListOrder.OldestFirst));

        // FIXME: This has to support the limit functionality
        const listData = b.primitive("SortAndGroupCalendarItems", {
            dateTime: b.getProperty(dateTimeProperty, b.getContext, table),
            endDateTime: definedMap(endDateTimeProperty, p => b.getProperty(p, b.getContext, table)),
            title,
            description,
            itemID: b.getProperty(rowIndexColumnName, b.getContext, table),
            list: arrayData,
            order,
        });
        return b.ui("CalendarList", {
            list: listData,
            emptyMessage: makeEmptyMessage(b, searchVariable, inlineListBuild, "noUpcomingEvents"),
            caption,
            defaultMode: b.constant(defaultModeProperty),
            order,
            onTap,
            insideInlineList: b.constant(inlineListBuild !== undefined),
            showTime: b.constant(showTime),
        });
    }

    public getBasicSearchProperties(desc: CalendarListArrayContentDescription): readonly string[] {
        const titleProperty = getColumnProperty(desc.titleProperty);
        if (titleProperty === undefined) return [];
        return [titleProperty];
    }

    protected get canReverse(): boolean {
        return false;
    }

    public getNeedsOrder(): boolean {
        return false;
    }

    // FIXME: Make sure we support limit when we move to NCM.  The issue is
    // that the calendar itself does the sorting, which means that it happens
    // after limiting, but it has to happen before.
    protected get supportsLimit(): boolean {
        return false;
    }

    public getContentPropertyDescriptors<T extends CalendarListArrayContentDescription>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        insideInlineList: boolean,
        desc: T,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean,
        withTransforms: boolean,
        array: ValueProducer | undefined
    ): readonly PropertyDescriptor[] {
        const descriptors: PropertyDescriptor[] = [
            ...super.getContentPropertyDescriptors(
                getPropertyTable,
                insideInlineList,
                desc,
                ccc,
                mutatingScreenKind,
                isDefaultArrayScreen,
                withTransforms,
                array
            ),
            makeTitlePropertyDescriptor(getPropertyTable, true, false),
            makeDescriptionPropertyDescriptor(getPropertyTable, PropertySection.Data),
            makeStringyColumnPropertyDescriptor(
                "dateTimeProperty",
                "Start",
                ["required", "editable", "searchable"],
                PropertySection.Data,
                getPropertyTable,
                dateTimeProperties
            ),
            makeEndDateTimePropertyDescriptor(getPropertyTable, PropertySection.Data),
            {
                kind: PropertyKind.Enum,
                property: { name: "order" },
                label: "Order",
                menuLabel: "Order of events",
                section: PropertySection.Data,
                cases: [
                    {
                        value: CalendarListOrder.OldestFirst,
                        label: "Oldest first",
                    },
                    {
                        value: CalendarListOrder.NewestFirst,
                        label: "Newest first",
                    },
                ],
                defaultCaseValue: CalendarListOrder.OldestFirst,
                visual: "dropdown",
            },
        ];
        if (!insideInlineList) {
            descriptors.push({
                kind: PropertyKind.Enum,
                property: { name: "defaultModeProperty" },
                label: "Default Screen",
                menuLabel: "Default view",
                cases: [
                    {
                        value: CalendarListMode.Calendar,
                        label: "Calendar",
                        // FIXME: Icon
                        icon: "calendar",
                    },
                    {
                        value: CalendarListMode.List,
                        label: "List",
                        icon: "list",
                    },
                ],
                defaultCaseValue: CalendarListMode.List,
                section: PropertySection.Design,
                visual: "small-images",
            });
        }
        return descriptors;
    }
}
